import { NgModule } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
// Interceptors
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { RouterLink, RouterLinkActive, RouterModule } from '@angular/router';

//Routes
import { routes } from './app.route';

import { AppComponent } from './app.component';

// service
import { AppService } from './service/app.service';

// store
import { StoreModule } from '@ngrx/store';
import { indexReducer } from './store/index.reducer';

// i18n
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

// perfect-scrollbar
import { NgScrollbarModule } from 'ngx-scrollbar';

// apexchart
import { NgApexchartsModule } from 'ng-apexcharts';

// highlightjs
import { HIGHLIGHT_OPTIONS, HighlightModule } from 'ngx-highlightjs';

// tippy
import { NgxTippyModule } from 'ngx-tippy-wrapper';

// headlessui
import { MenuModule } from 'headlessui-angular';

// modal
import { ModalModule } from 'angular-custom-modal';

// sortable
import { SortablejsModule } from '@dustfoundation/ngx-sortablejs';

// quill editor
import { QuillModule } from 'ngx-quill';

// dashboard
import { IndexComponent } from './index';

// Layouts
import { AppLayout } from './layouts/app-layout';
import { AuthLayout } from './layouts/auth-layout';

import { HeaderComponent } from './layouts/header';
import { FooterComponent } from './layouts/footer';
import { SidebarComponent } from './layouts/sidebar';
import { ThemeCustomizerComponent } from './layouts/theme-customizer';
import { IconModule } from './shared/icon/icon.module';
import { UsersComponent } from './pages/users/users.component';
import { MerchantsComponent } from './pages/merchants/merchants.component';
import { ProvidersComponent } from './pages/providers/providers.component';
import { TransactionsComponent } from './pages/transactions/transactions.component';
import { TransactionDetailsComponent } from './pages/transactionDetails/transactionDetails.component';
import { VirtualAccountsComponent } from './pages/virtualAccounts/virtualAccounts.component';

import { DepositsComponent } from './pages/deposits/deposits.component';
import { MerchantProductsComponent } from './pages/merchantProducts/merchantProducts.component';
import { ProductsComponent } from './pages/products/products.component';
import { CategoriesComponent } from './pages/categories/categories.component';
import { ComponentsModule } from './components/components.module';
import { DataTableModule } from '@bhplugin/ng-datatable';
import { NgSelectModule } from '@ng-select/ng-select';
import { Ng2FlatpickrModule } from 'ng2-flatpickr';
import { AngJson2excelBtnModule } from 'ang-json2excel-btn';
import { AuthInterceptor } from './services/auth.interceptor';
import { UserEffects } from './services/users/user.effects';
import { EffectsModule } from '@ngrx/effects';
import { userReducer } from './services/users/user.reducer';

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled' }),
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpTranslateLoader,
        deps: [HttpClient],
      },
    }),
    MenuModule,
    StoreModule.forRoot({
      index: indexReducer,
      userState: userReducer,
    }),
    NgxTippyModule,
    NgApexchartsModule,
    NgScrollbarModule.withConfig({
      visibility: 'hover',
      appearance: 'standard',
    }),
    HighlightModule,
    SortablejsModule,
    ModalModule,
    QuillModule.forRoot(),
    IconModule,
    ComponentsModule,
    DataTableModule,
    NgSelectModule,
    Ng2FlatpickrModule,
    AngJson2excelBtnModule,
    NgOptimizedImage,
    // TranslateModule,
    // RouterLink,
    // RouterLinkActive,
    // NgScrollbarModule,
    EffectsModule.forRoot([UserEffects]),
    TranslateModule,
    RouterLink,
    RouterLinkActive,
  ],
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    SidebarComponent,
    ThemeCustomizerComponent,
    IndexComponent,
    AppLayout,
    AuthLayout,
    UsersComponent,
    MerchantsComponent,
    ProvidersComponent,
    MerchantProductsComponent,
    TransactionsComponent,
    TransactionDetailsComponent,
    VirtualAccountsComponent,
    ProductsComponent,
    CategoriesComponent,
    DepositsComponent,
  ],

  providers: [
    AppService,
    Title,
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        coreLibraryLoader: () => import('highlight.js/lib/core'),
        languages: {
          json: () => import('highlight.js/lib/languages/json'),
          typescript: () => import('highlight.js/lib/languages/typescript'),
          xml: () => import('highlight.js/lib/languages/xml'),
        },
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

// AOT compilation support
export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
